<template>
  <v-container fluid class="pa-4">
    <div class="d-flex align-center mb-4">
      <v-icon icon="mdi-robot" size="28" class="mr-2" color="primary"></v-icon>
      <h2 class="text-h5 mb-0">Đăng sản phẩm tự động</h2>
    </div>

    <v-alert v-if="globalError" type="error" variant="tonal" closable class="mb-4"
      @click:close="globalError = ''">
      {{ globalError }}
    </v-alert>

    <v-stepper v-model="step" alt-labels>
      <v-stepper-header>
        <v-stepper-item title="Quét & chụp ảnh" :value="1" :complete="step > 1" />
        <v-divider />
        <v-stepper-item title="Chọn ảnh" :value="2" :complete="step > 2" />
        <v-divider />
        <v-stepper-item title="Nội dung GitHub" :value="3" :complete="step > 3" />
        <v-divider />
        <v-stepper-item title="Thông tin" :value="4" :complete="step > 4" />
        <v-divider />
        <v-stepper-item title="Xem lại & đăng" :value="5" />
      </v-stepper-header>

      <v-stepper-window>
        <!-- BƯỚC 1: quét trang preview -->
        <v-stepper-window-item :value="1">
          <v-card flat>
            <v-card-text>
              <p class="text-body-2 text-medium-emphasis mb-4">
                Nhập link preview và tài khoản đăng nhập. Hệ thống sẽ tự đăng nhập, dò các link
                trong menu rồi chụp lại mỗi trang một ảnh (khung hình 1920x1080).
              </p>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="crawlForm.url" label="Link preview sản phẩm *"
                    placeholder="https://demo-erp.vercel.app" variant="outlined" density="comfortable"
                    prepend-inner-icon="mdi-link-variant" :disabled="crawling" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model.number="crawlForm.maxPages" label="Số trang tối đa"
                    type="number" min="1" max="30" variant="outlined" density="comfortable"
                    hint="Tối đa 30 trang" persistent-hint :disabled="crawling" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="crawlForm.username" label="Tài khoản đăng nhập"
                    variant="outlined" density="comfortable" prepend-inner-icon="mdi-account"
                    :disabled="crawling" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="crawlForm.password" label="Mật khẩu"
                    :type="showPassword ? 'text' : 'password'" variant="outlined" density="comfortable"
                    prepend-inner-icon="mdi-lock"
                    :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append-inner="showPassword = !showPassword" :disabled="crawling" />
                </v-col>
              </v-row>

              <v-expansion-panels variant="accordion" class="mt-2">
                <v-expansion-panel title="Tuỳ chọn nâng cao (chỉ dùng khi tự dò form thất bại)">
                  <v-expansion-panel-text>
                    <p class="text-caption text-medium-emphasis mb-3">
                      Mặc định hệ thống tự tìm ô mật khẩu rồi suy ra ô tài khoản và nút đăng nhập.
                      Nếu trang có form lạ, điền CSS selector vào đây.
                    </p>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="crawlForm.usernameSelector" label="Selector ô tài khoản"
                          placeholder="#email" variant="outlined" density="compact" />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="crawlForm.passwordSelector" label="Selector ô mật khẩu"
                          placeholder="#password" variant="outlined" density="compact" />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="crawlForm.submitSelector" label="Selector nút đăng nhập"
                          placeholder="button[type=submit]" variant="outlined" density="compact" />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-alert v-if="crawling" type="info" variant="tonal" class="mt-4">
                <div class="d-flex align-center">
                  <v-progress-circular indeterminate size="20" width="2" class="mr-3" />
                  <div>
                    <div>Đang đăng nhập và chụp ảnh... Quá trình này có thể mất vài phút, đừng đóng trang.</div>
                    <div v-if="crawlProgress" class="text-caption mt-1">{{ crawlProgress }}</div>
                  </div>
                </div>
              </v-alert>

              <v-alert v-if="crawlWarnings.length" type="warning" variant="tonal" class="mt-4">
                <div class="font-weight-medium mb-1">Một số trang chụp không thành công:</div>
                <ul class="ml-4">
                  <li v-for="f in crawlWarnings" :key="f.url" class="text-caption">
                    {{ f.title || f.url }} — {{ f.error }}
                  </li>
                </ul>
              </v-alert>

              <!-- Ảnh chụp màn hình lúc lỗi, do server gửi về -->
              <v-card v-if="debugImage" variant="outlined" class="mt-4">
                <v-card-title class="text-subtitle-2">
                  <v-icon start size="small" color="error">mdi-image-search</v-icon>
                  Ảnh màn hình trang preview tại thời điểm lỗi
                </v-card-title>
                <v-card-text>
                  <a :href="storageBase + debugImage" target="_blank" rel="noopener">
                    <v-img :src="storageBase + debugImage" max-height="420" class="rounded border" />
                  </a>
                  <div class="text-caption text-medium-emphasis mt-1">Bấm vào ảnh để mở kích thước đầy đủ.</div>
                </v-card-text>
              </v-card>

              <!-- Bảng log chi tiết -->
              <v-card v-if="logs.length" variant="outlined" class="mt-4">
                <v-toolbar density="compact" color="grey-darken-4">
                  <v-icon class="ml-3" size="small" color="grey-lighten-1">mdi-console-line</v-icon>
                  <v-toolbar-title class="text-body-2">
                    Log chi tiết ({{ logs.length }} dòng)
                  </v-toolbar-title>
                  <v-spacer />
                  <v-checkbox-btn v-model="showDebugLogs" label="Hiện debug" density="compact"
                    class="text-caption mr-2" hide-details />
                  <v-checkbox-btn v-model="autoScrollLogs" label="Tự cuộn" density="compact"
                    class="text-caption mr-2" hide-details />
                  <v-btn size="small" variant="text" @click="copyLogs">
                    <v-icon start size="small">mdi-content-copy</v-icon>Chép
                  </v-btn>
                  <v-btn size="small" variant="text" @click="downloadLogs">
                    <v-icon start size="small">mdi-download</v-icon>Tải
                  </v-btn>
                  <v-btn size="small" variant="text" @click="logs = []">
                    <v-icon size="small">mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>
                <div ref="logBox" class="autopost-log">
                  <div v-for="l in visibleLogs" :key="l.seq" class="autopost-log__row" :class="'is-' + l.level">
                    <span class="autopost-log__time">+{{ (l.ms / 1000).toFixed(1) }}s</span>
                    <span class="autopost-log__step">{{ l.step }}</span>
                    <span class="autopost-log__msg">{{ l.message }}</span>
                    <button v-if="l.detail" class="autopost-log__more" @click="toggleDetail(l.seq)">
                      {{ openDetails.includes(l.seq) ? '▾ ẩn' : '▸ chi tiết' }}
                    </button>
                    <pre v-if="l.detail && openDetails.includes(l.seq)"
                      class="autopost-log__detail">{{ formatDetail(l.detail) }}</pre>
                  </div>
                </div>
              </v-card>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
              <v-btn variant="text" size="small" :loading="checkingHealth" @click="checkHealth">
                <v-icon start>mdi-stethoscope</v-icon>
                Kiểm tra server
              </v-btn>
              <v-spacer />
              <v-btn color="primary" variant="flat" :loading="crawling"
                :disabled="!crawlForm.url" @click="startCrawl">
                <v-icon start>mdi-camera-iris</v-icon>
                Quét & chụp ảnh
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-window-item>

        <!-- BƯỚC 2: chọn ảnh + thumbnail -->
        <v-stepper-window-item :value="2">
          <v-card flat>
            <v-card-text>
              <div class="d-flex align-center flex-wrap mb-4 ga-2">
                <v-chip color="primary" variant="tonal">
                  <v-icon start>mdi-image-multiple</v-icon>
                  {{ images.length }} ảnh slider
                </v-chip>
                <v-chip v-if="thumbnail" color="success" variant="tonal">
                  <v-icon start>mdi-check</v-icon>
                  Đã có thumbnail 400px
                </v-chip>
                <v-spacer />
                <span class="text-caption text-medium-emphasis">
                  Bỏ ảnh không ưng ý, rồi bấm "Chọn làm thumbnail" trên 1 ảnh.
                </span>
              </div>

              <v-alert v-if="!images.length" type="warning" variant="tonal">
                Chưa có ảnh nào. Quay lại bước 1 để chụp.
              </v-alert>

              <v-row>
                <v-col v-for="img in images" :key="img.image" cols="12" sm="6" md="4">
                  <v-card variant="outlined"
                    :color="thumbnailSource === img.image ? 'primary' : undefined">
                    <v-img :src="storageBase + img.image" aspect-ratio="1.777" cover>
                      <template v-slot:placeholder>
                        <div class="d-flex align-center justify-center fill-height">
                          <v-progress-circular indeterminate color="grey-lighten-2" />
                        </div>
                      </template>
                    </v-img>
                    <v-card-text class="py-2">
                      <div class="text-body-2 font-weight-medium text-truncate">{{ img.title }}</div>
                      <div class="text-caption text-medium-emphasis text-truncate">{{ img.url }}</div>
                    </v-card-text>
                    <v-card-actions class="pt-0">
                      <v-btn size="small" variant="text"
                        :color="thumbnailSource === img.image ? 'primary' : 'default'"
                        :loading="thumbnailLoading === img.image"
                        @click="chooseThumbnail(img)">
                        <v-icon start size="18">mdi-star</v-icon>
                        {{ thumbnailSource === img.image ? 'Thumbnail' : 'Chọn làm thumbnail' }}
                      </v-btn>
                      <v-spacer />
                      <v-btn size="small" variant="text" color="error" icon
                        @click="removeImage(img)">
                        <v-icon size="18">mdi-delete</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <div v-if="thumbnail" class="mt-4">
                <div class="text-body-2 font-weight-medium mb-2">Thumbnail đã tạo (400px):</div>
                <v-img :src="storageBase + thumbnail" max-width="400" class="rounded border" />
              </div>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
              <v-btn variant="text" @click="step = 1">Quay lại</v-btn>
              <v-spacer />
              <v-btn color="primary" variant="flat" :disabled="!images.length"
                @click="goToGithub">Tiếp tục</v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-window-item>

        <!-- BƯỚC 3: nội dung từ GitHub -->
        <v-stepper-window-item :value="3">
          <v-card flat>
            <v-card-text>
              <v-alert v-if="githubError" type="error" variant="tonal" class="mb-4">
                {{ githubError }}
              </v-alert>

              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete v-model="selectedRepo" :items="repos" item-title="full_name"
                    item-value="full_name" label="Chọn repository" variant="outlined"
                    density="comfortable" prepend-inner-icon="mdi-github" :loading="loadingRepos"
                    @update:model-value="loadFolders">
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props" :title="item.raw.full_name">
                        <template v-slot:append>
                          <v-chip v-if="item.raw.private" size="x-small" color="warning"
                            variant="tonal">private</v-chip>
                        </template>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-autocomplete v-model="selectedFolder" :items="folders" item-title="name"
                    item-value="path" label="Chọn thư mục sản phẩm" variant="outlined"
                    density="comfortable" prepend-inner-icon="mdi-folder"
                    :loading="loadingFolders" :disabled="!selectedRepo"
                    @update:model-value="loadFolderContent" />
                </v-col>
              </v-row>

              <v-alert v-if="contentMissing.length" type="warning" variant="tonal" density="compact"
                class="mb-4">
                Thư mục này thiếu file: {{ contentMissing.join(', ') }}. Bạn có thể tự nhập bên dưới.
              </v-alert>

              <div v-if="loadingContent" class="d-flex align-center my-4">
                <v-progress-circular indeterminate size="20" width="2" class="mr-3" />
                Đang đọc nội dung...
              </div>

              <div class="mb-2 d-flex align-center">
                <span class="text-body-2 font-weight-medium">Mô tả sản phẩm (description.txt)</span>
                <v-spacer />
                <v-btn size="x-small" variant="text" @click="showDescPreview = !showDescPreview">
                  {{ showDescPreview ? 'Sửa nội dung' : 'Xem thử' }}
                </v-btn>
              </div>
              <div v-if="showDescPreview" class="pa-3 rounded border mb-4 desc-preview"
                v-html="form.description"></div>
              <v-textarea v-else v-model="form.description" variant="outlined" rows="8"
                auto-grow class="mb-4" placeholder="Nội dung mô tả, hỗ trợ HTML" />

              <div class="text-body-2 font-weight-medium mb-2">Template email (email.txt)</div>
              <v-textarea v-model="form.template_email" variant="outlined" rows="6" auto-grow
                placeholder="Nội dung email gửi khách sau khi mua" />
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
              <v-btn variant="text" @click="step = 2">Quay lại</v-btn>
              <v-spacer />
              <v-btn color="primary" variant="flat" @click="step = 4">Tiếp tục</v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-window-item>

        <!-- BƯỚC 4: thông tin nhập tay -->
        <v-stepper-window-item :value="4">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field v-model="form.name" label="Tên sản phẩm *" variant="outlined"
                    density="comfortable" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select v-model="form.category_id" :items="categories" item-title="name"
                    item-value="id" label="Danh mục *" variant="outlined" density="comfortable" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model.number="form.price" label="Giá gốc (đ) *" type="number"
                    variant="outlined" density="comfortable" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model.number="form.sale_off" label="Giảm giá (%)" type="number"
                    min="0" max="100" variant="outlined" density="comfortable" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model.number="form.affiliate_share" label="Hoa hồng CTV (%)"
                    type="number" min="0" max="100" variant="outlined" density="comfortable" />
                </v-col>
                <v-col cols="12" md="8">
                  <v-combobox v-model="form.tags" label="Tags" variant="outlined"
                    density="comfortable" multiple chips closable-chips clearable
                    hint="Gõ tag rồi nhấn Enter" persistent-hint />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-checkbox v-model="form.is_license" label="Sản phẩm có License (Key)"
                    color="primary" hide-details />
                </v-col>
              </v-row>

              <v-alert v-if="form.sale_off > 0 && form.price > 0" type="info" variant="tonal"
                density="compact" class="mt-2">
                Giá sau giảm: <strong>{{ formatPrice(finalPrice) }}</strong>
              </v-alert>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
              <v-btn variant="text" @click="step = 3">Quay lại</v-btn>
              <v-spacer />
              <v-btn color="primary" variant="flat" @click="step = 5">Xem lại</v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-window-item>

        <!-- BƯỚC 5: preview & đăng -->
        <v-stepper-window-item :value="5">
          <v-card flat>
            <v-card-text>
              <v-alert v-if="validationErrors.length" type="error" variant="tonal" class="mb-4">
                <div class="font-weight-medium mb-1">Cần bổ sung trước khi đăng:</div>
                <ul class="ml-4">
                  <li v-for="e in validationErrors" :key="e" class="text-caption">{{ e }}</li>
                </ul>
              </v-alert>

              <v-row>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis mb-1">Thumbnail</div>
                  <v-img v-if="thumbnail" :src="storageBase + thumbnail" class="rounded border mb-4" />
                  <div v-else class="pa-4 rounded border text-caption text-medium-emphasis mb-4">
                    Chưa chọn thumbnail
                  </div>

                  <v-list density="compact" class="rounded border">
                    <v-list-item title="Tên" :subtitle="form.name || '—'" />
                    <v-list-item title="Danh mục" :subtitle="categoryName || '—'" />
                    <v-list-item title="Giá gốc" :subtitle="formatPrice(form.price)" />
                    <v-list-item title="Giảm giá" :subtitle="(form.sale_off || 0) + '%'" />
                    <v-list-item title="Giá bán" :subtitle="formatPrice(finalPrice)" />
                    <v-list-item title="Hoa hồng CTV" :subtitle="(form.affiliate_share || 0) + '%'" />
                    <v-list-item title="License" :subtitle="form.is_license ? 'Có' : 'Không'" />
                    <v-list-item title="Tags" :subtitle="tagsText || '—'" />
                    <v-list-item title="Số ảnh slider" :subtitle="String(images.length)" />
                    <v-list-item title="Source zip"
                      :subtitle="zipInfo ? zipInfo.name + ' (' + formatSize(zipInfo.size) + ')' : 'Không có file zip trong thư mục'" />
                  </v-list>
                </v-col>

                <v-col cols="12" md="8">
                  <div class="text-caption text-medium-emphasis mb-1">Ảnh slider</div>
                  <v-slide-group show-arrows class="mb-4">
                    <v-slide-group-item v-for="img in images" :key="img.image">
                      <v-img :src="storageBase + img.image" width="260" class="ma-1 rounded border" />
                    </v-slide-group-item>
                  </v-slide-group>

                  <div class="text-caption text-medium-emphasis mb-1">Mô tả</div>
                  <div class="pa-3 rounded border mb-4 desc-preview" v-html="form.description || '<em>Chưa có mô tả</em>'"></div>

                  <div class="text-caption text-medium-emphasis mb-1">Template email</div>
                  <div class="pa-3 rounded border desc-preview" v-html="form.template_email || '<em>Chưa có template email</em>'"></div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
              <v-btn variant="text" @click="step = 4">Quay lại</v-btn>
              <v-spacer />
              <v-btn color="success" variant="flat" size="large" :loading="publishing"
                :disabled="validationErrors.length > 0" @click="publish">
                <v-icon start>mdi-cloud-upload</v-icon>
                Đăng sản phẩm
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>

    <!-- Đăng thành công -->
    <v-dialog v-model="successDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-check-circle" color="success" class="mr-2" />
          Đăng thành công
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            Sản phẩm <strong>{{ publishedName }}</strong> đã được tạo (ID {{ publishedId }}).
          </p>

          <v-divider class="mb-4" />

          <div class="text-body-2 font-weight-medium mb-2">Đẩy source zip sang hệ thống deploy</div>

          <v-alert v-if="!selectedRepo || !selectedFolder" type="info" variant="tonal"
            density="compact">
            Không có thư mục GitHub nào được chọn nên không có file zip để đẩy.
          </v-alert>

          <template v-else>
            <div class="text-caption text-medium-emphasis mb-2">
              Thư mục: <code>{{ selectedRepo }}/{{ selectedFolder }}</code>
              <span v-if="zipInfo"> — file <strong>{{ zipInfo.name }}</strong></span>
            </div>

            <v-alert v-if="pushResult" :type="pushResult.ok ? 'success' : 'error'" variant="tonal"
              density="compact" class="mb-3">
              {{ pushResult.message }}
              <div v-if="pushResult.zip" class="text-caption mt-1">
                {{ pushResult.zip.name }} — {{ formatSize(pushResult.zip.sizeBytes) }}
              </div>
            </v-alert>

            <v-btn :color="pushResult && pushResult.ok ? 'success' : 'primary'"
              :variant="pushResult && pushResult.ok ? 'tonal' : 'flat'" :loading="pushing"
              :disabled="pushResult && pushResult.ok" @click="pushSource">
              <v-icon start>mdi-cloud-upload-outline</v-icon>
              {{ pushResult && pushResult.ok ? 'Đã đẩy source' : (pushResult ? 'Thử lại' : 'Đẩy source zip') }}
            </v-btn>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="resetAll">Đăng sản phẩm khác</v-btn>
          <v-spacer />
          <v-btn color="primary" variant="flat" @click="$router.push('/admin/product')">
            Xem danh sách
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from '@/http';
import autoPostService from '@/services/autoPostService';

export default {
  name: 'AutoPost',
  data() {
    return {
      step: 1,
      globalError: '',
      showPassword: false,

      // Bước 1
      crawlForm: {
        url: '',
        username: '',
        password: '',
        maxPages: 15,
        usernameSelector: '',
        passwordSelector: '',
        submitSelector: ''
      },
      crawling: false,
      crawlWarnings: [],
      crawlProgress: '',

      // Log chi tiết của lần quét
      logs: [],
      lastLogSeq: 0,
      openDetails: [],
      showDebugLogs: false,
      autoScrollLogs: true,
      debugImage: null,
      currentJobId: '',
      checkingHealth: false,

      // Bước 2
      images: [],
      thumbnail: '',
      thumbnailSource: '',
      thumbnailLoading: '',

      // Bước 3
      repos: [],
      folders: [],
      selectedRepo: null,
      selectedFolder: null,
      loadingRepos: false,
      loadingFolders: false,
      loadingContent: false,
      githubError: '',
      contentMissing: [],
      showDescPreview: false,

      // Bước 4
      categories: [],
      form: {
        name: '',
        price: 0,
        category_id: null,
        tags: [],
        sale_off: 0,
        affiliate_share: 0,
        is_license: false,
        description: '',
        template_email: ''
      },

      // Bước 5
      publishing: false,
      successDialog: false,
      publishedName: '',
      publishedId: null,

      // Đẩy source zip sau khi tạo sản phẩm
      zipInfo: null,
      pushing: false,
      pushResult: null,

      snackbar: { show: false, text: '', color: 'success' }
    };
  },
  computed: {
    // Ảnh nằm trên chính server API, suy ra host từ baseURL để không lệch local/production
    storageBase() {
      return (axios.defaults.baseURL || '').replace(/\/api\/?$/, '');
    },
    // Log mức debug khá ồn (console của trang, request hỏng...) nên mặc định ẩn
    visibleLogs() {
      return this.showDebugLogs ? this.logs : this.logs.filter(l => l.level !== 'debug');
    },
    finalPrice() {
      const price = Number(this.form.price) || 0;
      const off = Number(this.form.sale_off) || 0;
      return off > 0 ? Math.round(price * (1 - off / 100)) : price;
    },
    categoryName() {
      const c = this.categories.find(x => x.id === this.form.category_id);
      return c ? c.name : '';
    },
    tagsText() {
      return Array.isArray(this.form.tags) ? this.form.tags.join(', ') : '';
    },
    validationErrors() {
      const errs = [];
      if (!this.form.name || !this.form.name.trim()) errs.push('Chưa nhập tên sản phẩm');
      if (!this.form.category_id) errs.push('Chưa chọn danh mục');
      if (!this.form.price || this.form.price <= 0) errs.push('Chưa nhập giá sản phẩm');
      if (!this.images.length) errs.push('Chưa có ảnh slider nào');
      if (!this.thumbnail) errs.push('Chưa chọn ảnh làm thumbnail');
      return errs;
    }
  },
  mounted() {
    this.fetchCategories();
  },
  methods: {
    notify(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
    formatPrice(v) {
      const n = Number(v) || 0;
      return n.toLocaleString('vi-VN') + ' đ';
    },
    formatSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
      return (n / 1024 / 1024).toFixed(2) + ' MB';
    },
    errMsg(error, fallback) {
      return (error.response && error.response.data && error.response.data.message)
        || error.message || fallback;
    },

    async fetchCategories() {
      try {
        const res = await axios.get('/categories');
        this.categories = res.data || [];
      } catch (error) {
        this.globalError = 'Không tải được danh mục: ' + this.errMsg(error, '');
      }
    },

    // ----- Log -----
    pushLogs(entries) {
      if (!entries || !entries.length) return;
      this.logs.push(...entries);
      this.lastLogSeq = entries[entries.length - 1].seq;
      if (this.autoScrollLogs) {
        this.$nextTick(() => {
          const box = this.$refs.logBox;
          if (box) box.scrollTop = box.scrollHeight;
        });
      }
    },
    toggleDetail(seq) {
      const i = this.openDetails.indexOf(seq);
      if (i >= 0) this.openDetails.splice(i, 1);
      else this.openDetails.push(seq);
    },
    formatDetail(detail) {
      try {
        return JSON.stringify(detail, null, 2);
      } catch (e) {
        return String(detail);
      }
    },
    logsAsText() {
      return this.logs.map(l => {
        const head = `[+${(l.ms / 1000).toFixed(1)}s] ${String(l.level).toUpperCase().padEnd(7)} `
          + `${String(l.step).padEnd(10)} ${l.message}`;
        return l.detail ? `${head}\n    ${this.formatDetail(l.detail)}` : head;
      }).join('\n');
    },
    async copyLogs() {
      try {
        await navigator.clipboard.writeText(this.logsAsText());
        this.notify('Đã chép log vào clipboard');
      } catch (e) {
        this.notify('Trình duyệt chặn clipboard, dùng nút "Tải" nhé', 'warning');
      }
    },
    downloadLogs() {
      const blob = new Blob([this.logsAsText()], { type: 'text/plain;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `auto-post-${this.currentJobId || 'log'}.txt`;
      a.click();
      URL.revokeObjectURL(a.href);
    },

    // Kiểm tra nhanh môi trường server: Chrome, thư mục ảnh
    async checkHealth() {
      this.checkingHealth = true;
      this.globalError = '';
      this.logs = [];
      this.lastLogSeq = 0;
      try {
        const res = await autoPostService.health();
        const d = res.data.data || {};
        this.pushLogs(d.logs || []);
        this.pushLogs([{
          seq: 900000, ms: 0, level: d.chrome && d.chrome.ok ? 'success' : 'error',
          step: 'health',
          message: d.chrome && d.chrome.ok ? `Chrome OK — ${d.chrome.version}` : `Chrome LỖI — ${d.chrome && d.chrome.message}`,
          detail: d
        }]);
        this.pushLogs([{
          seq: 900001, ms: 0, level: d.uploadDir && d.uploadDir.ok ? 'success' : 'error',
          step: 'health',
          message: d.uploadDir && d.uploadDir.ok
            ? `Thư mục ảnh ghi được — ${d.uploadDir.path} (${d.uploadDir.soFile} file)`
            : `Thư mục ảnh LỖI — ${d.uploadDir && d.uploadDir.message}`,
          detail: d.uploadDir
        }]);
        this.lastLogSeq = 0;
      } catch (error) {
        this.globalError = 'Kiểm tra server thất bại: ' + this.errMsg(error, '');
      } finally {
        this.checkingHealth = false;
      }
    },

    // ----- Bước 1 -----
    // Quét chạy nền: xin jobId rồi hỏi trạng thái mỗi 2 giây, kéo log về dần.
    // Làm vậy để không có request HTTP nào kéo dài vài phút — nginx trước Node
    // sẽ cắt request dài và trả trang 504 không có header CORS, khiến trình duyệt
    // chặn response và axios chỉ báo được "Network Error".
    async startCrawl() {
      this.crawling = true;
      this.globalError = '';
      this.crawlWarnings = [];
      this.logs = [];
      this.lastLogSeq = 0;
      this.openDetails = [];
      this.debugImage = null;
      this.crawlProgress = 'Đang gửi yêu cầu tới server...';
      try {
        const started = await autoPostService.startCrawl({ ...this.crawlForm });
        this.currentJobId = started.data.data.jobId;

        const data = await this.waitForCrawl(this.currentJobId);
        this.images = data.images || [];
        this.crawlWarnings = data.failed || [];

        if (!this.images.length) {
          this.globalError = 'Không chụp được ảnh nào. Xem log bên dưới để biết dừng ở bước nào.';
          return;
        }
        this.notify(`Đã chụp ${this.images.length} ảnh`);
        this.step = 2;
      } catch (error) {
        this.globalError = this.errMsg(error, 'Quét thất bại');
      } finally {
        this.crawling = false;
        this.crawlProgress = '';
      }
    },

    // Hỏi trạng thái job cho tới khi xong. Lỗi mạng lẻ tẻ thì thử lại,
    // chỉ bỏ cuộc sau 5 lần hỏi liên tiếp thất bại.
    async waitForCrawl(jobId) {
      const POLL_MS = 2000;
      const MAX_WAIT_MS = 20 * 60 * 1000;
      const deadline = Date.now() + MAX_WAIT_MS;
      let misses = 0;

      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, POLL_MS));
        let job;
        try {
          const res = await autoPostService.getCrawlStatus(jobId, this.lastLogSeq);
          job = res.data.data;
          misses = 0;
        } catch (error) {
          if (error.response && error.response.status === 404) {
            throw new Error('Job quét không còn tồn tại, có thể server đã khởi động lại.');
          }
          if (++misses >= 5) throw error;
          this.crawlProgress = `Mất kết nối tạm thời, đang thử lại (${misses}/5)...`;
          continue;
        }

        this.pushLogs(job.logs);
        if (job.debugImage) this.debugImage = job.debugImage;

        if (job.progress && job.progress.message) {
          const { done, total, message } = job.progress;
          this.crawlProgress = total ? `${message} (${done}/${total})` : message;
        }
        if (job.status === 'done') return job.result || {};
        if (job.status === 'error') throw new Error(job.message || 'Quét thất bại');
      }
      throw new Error('Quét quá lâu (>20 phút), đã dừng theo dõi. Xem log để biết đang kẹt ở bước nào.');
    },

    // ----- Bước 2 -----
    async removeImage(img) {
      try {
        await autoPostService.deleteImage(img.image);
      } catch (error) {
        // Xoá file lỗi thì vẫn bỏ khỏi danh sách, file thừa không ảnh hưởng sản phẩm
        console.warn('Không xoá được file:', this.errMsg(error, ''));
      }
      this.images = this.images.filter(i => i.image !== img.image);
      if (this.thumbnailSource === img.image) {
        this.thumbnailSource = '';
        this.thumbnail = '';
      }
    },

    async chooseThumbnail(img) {
      this.thumbnailLoading = img.image;
      try {
        const res = await autoPostService.makeThumbnail(img.image);
        this.thumbnail = res.data.data.thumbnail;
        this.thumbnailSource = img.image;
        this.notify('Đã tạo thumbnail 400px');
      } catch (error) {
        this.globalError = this.errMsg(error, 'Tạo thumbnail thất bại');
      } finally {
        this.thumbnailLoading = '';
      }
    },

    // ----- Bước 3 -----
    goToGithub() {
      this.step = 3;
      if (!this.repos.length) this.loadRepos();
    },

    async loadRepos() {
      this.loadingRepos = true;
      this.githubError = '';
      try {
        const res = await autoPostService.getRepos();
        this.repos = res.data.data || [];
      } catch (error) {
        this.githubError = this.errMsg(error, 'Không tải được danh sách repo');
      } finally {
        this.loadingRepos = false;
      }
    },

    async loadFolders(repo) {
      if (!repo) return;
      this.loadingFolders = true;
      this.githubError = '';
      this.folders = [];
      this.selectedFolder = null;
      try {
        const res = await autoPostService.getFolders(repo);
        this.folders = res.data.data || [];
        if (!this.folders.length) {
          this.githubError = 'Repo này không có thư mục con nào ở thư mục gốc.';
        }
      } catch (error) {
        this.githubError = this.errMsg(error, 'Không tải được thư mục');
      } finally {
        this.loadingFolders = false;
      }
    },

    async loadFolderContent(folder) {
      if (!folder || !this.selectedRepo) return;
      this.loadingContent = true;
      this.githubError = '';
      this.contentMissing = [];
      this.zipInfo = null;
      this.pushResult = null;

      // Kiểm tra sẵn thư mục có file zip không, lỗi ở đây không chặn việc đọc nội dung
      autoPostService.getZipInfo(this.selectedRepo, folder)
        .then(res => { this.zipInfo = res.data.data; })
        .catch(() => { this.zipInfo = null; });

      try {
        const res = await autoPostService.getFolderContent(this.selectedRepo, folder);
        const data = res.data.data || {};
        this.form.description = data.description || '';
        this.form.template_email = data.template_email || '';
        this.contentMissing = data.missing || [];

        // Gợi ý tên sản phẩm từ tên thư mục nếu chưa nhập
        if (!this.form.name) {
          const folderName = (this.folders.find(f => f.path === folder) || {}).name || '';
          this.form.name = folderName.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        }
        if (!this.contentMissing.length) this.notify('Đã lấy nội dung từ GitHub');
      } catch (error) {
        this.githubError = this.errMsg(error, 'Không đọc được nội dung thư mục');
      } finally {
        this.loadingContent = false;
      }
    },

    // ----- Bước 5 -----
    async publish() {
      if (this.validationErrors.length) return;
      this.publishing = true;
      this.globalError = '';
      try {
        const payload = {
          name: this.form.name.trim(),
          price: this.form.price,
          category_id: this.form.category_id,
          tags: Array.isArray(this.form.tags) ? this.form.tags.join(',') : '',
          sale_off: this.form.sale_off || 0,
          affiliate_share: this.form.affiliate_share || 0,
          is_license: this.form.is_license ? 1 : 0,
          description: this.form.description,
          template_email: this.form.template_email,
          thumbnail: this.thumbnail,
          slide_img: this.images.map(i => i.image)
        };
        const res = await autoPostService.publish(payload);
        this.publishedName = payload.name;
        this.publishedId = res.data.data ? res.data.data.id : null;
        this.successDialog = true;
      } catch (error) {
        this.globalError = this.errMsg(error, 'Đăng sản phẩm thất bại');
      } finally {
        this.publishing = false;
      }
    },

    // ----- Đẩy source zip -----
    async pushSource() {
      this.pushing = true;
      this.pushResult = null;
      try {
        const res = await autoPostService.pushSource({
          repo: this.selectedRepo,
          folder: this.selectedFolder,
          productId: this.publishedId,
          productName: this.publishedName
        });
        // Server luôn trả 200, cờ ok nằm trong data để hiện được lỗi chi tiết
        this.pushResult = res.data.data || { ok: false, message: res.data.message };
      } catch (error) {
        this.pushResult = { ok: false, message: this.errMsg(error, 'Đẩy source thất bại') };
      } finally {
        this.pushing = false;
      }
    },

    resetAll() {
      this.successDialog = false;
      this.step = 1;
      this.zipInfo = null;
      this.pushResult = null;
      this.images = [];
      this.thumbnail = '';
      this.thumbnailSource = '';
      this.crawlWarnings = [];
      this.selectedFolder = null;
      this.contentMissing = [];
      this.form = {
        name: '', price: 0, category_id: null, tags: [], sale_off: 0,
        affiliate_share: 0, is_license: false, description: '', template_email: ''
      };
    }
  }
};
</script>

<style scoped>
.desc-preview {
  max-height: 320px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.02);
}

.desc-preview :deep(img) {
  max-width: 100%;
  height: auto;
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* ----- Bảng log của bước quét ----- */
.autopost-log {
  max-height: 420px;
  overflow-y: auto;
  background: #11151a;
  color: #cfd6dd;
  font-family: 'Consolas', 'Menlo', 'DejaVu Sans Mono', monospace;
  font-size: 12px;
  line-height: 1.55;
  padding: 8px 12px;
}

.autopost-log__row {
  padding: 1px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  word-break: break-word;
}

.autopost-log__time {
  color: #6c7a89;
  margin-right: 8px;
  display: inline-block;
  min-width: 52px;
  text-align: right;
}

.autopost-log__step {
  color: #7aa2f7;
  margin-right: 8px;
  display: inline-block;
  min-width: 72px;
}

.autopost-log__row.is-success .autopost-log__msg { color: #9ece6a; }
.autopost-log__row.is-warn .autopost-log__msg { color: #e0af68; }
.autopost-log__row.is-error .autopost-log__msg { color: #f7768e; font-weight: 600; }
.autopost-log__row.is-debug { opacity: 0.6; }

.autopost-log__more {
  margin-left: 8px;
  color: #7dcfff;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  padding: 0;
}

.autopost-log__detail {
  margin: 4px 0 6px 60px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.05);
  border-left: 2px solid #7aa2f7;
  border-radius: 3px;
  white-space: pre-wrap;
  color: #a9b1ba;
  max-height: 260px;
  overflow: auto;
}
</style>
